<template>
  <div v-loading="loading" class="preview-matrix">
    <div v-if="matrix.setting.productivity_size && matrix.setting.potential_size">
      <div v-if="matrix && matrix.cells && matrix.setting" class="matrix-block">
        <p class="potential_side" v-if="showMatrix">Потенциал <span>&#8594;</span></p>
        <p class="productivity_side" v-if="showMatrix">Производительность <span>&#8594;</span></p>
        <div class="matrix" v-loading="loading">
          <div v-for="i in matrix.setting.potential_size" :key="'row-' + i" class="row">
            <div
              v-for="j in matrix.setting.productivity_size"
              v-if="getCell(i, j)"
              :key="'cell-' + i + '-' + j"
              class="cell"
              :style="{'background-color': getCell(i, j).color}"
              draggable="true"
              @dragstart="onDragStart(i, j)"
              @dragend="onDragEnd"
              @dragenter.prevent="onDragEnter(i, j)"
              @dragleave="onDragLeave(i, j)"
              @dragover.prevent="onDragOver(i, j)"
              @drop="onDrop(i, j)"
              :class="{
                dragging: draggedCell && draggedCell.row === i && draggedCell.column === j,
                'drop-target': hoverTarget && hoverTarget.row === i && hoverTarget.column === j
              }"
            >
              <div class="header-cell">
                <div class="description-cell">
                  <div class="name">
                    <collapse-text-with-tooltip
                      :text="getCell(i, j).name"
                      :maxLength="10"
                      trigger="click"
                    ></collapse-text-with-tooltip>
                  </div>
                  <el-popover
                    v-if="getCell(i, j).description !== ''"
                    placement="top"
                    trigger="click"
                  >
                    <el-button
                      slot="reference"
                      style="margin-left:10px"
                      class="table-action-button danger"
                      type="text"
                      round
                      size="mini"
                      icon="fas fa-info-circle"
                    ></el-button>
                    <div>{{ getCell(i, j).description }}</div>
                  </el-popover>
                </div>
                <div class="move-icon">
                  <i class="fas fa-arrows-alt"></i>
                </div>
                <div>
                  <el-button
                    v-if="$canAndRulesPass(matrix, 'talent_matrix.matrix_update')"
                    icon="fas fa-cog"
                    size="mini"
                    type="text"
                    @click="openEditDialog(getCell(i, j))"
                  ></el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div style="text-align: left; color: #909399; font-weight: bold">
        Невозможно построить матрицу, пока не выбран ее размер.<br>
        Потенциал (ось Y) и Производительность (ось X).
      </div>
    </div>
    <talent-matrix-cell-edit-dialog
      ref="editDialog"
      @cell-update="cellUpdate()"
    >
    </talent-matrix-cell-edit-dialog>
  </div>
</template>

<script>
import TalentMatrixCellEditDialog from "@/modules/talentMatrix/components/TalentMatrixCellEditDialog.vue";
import CollapseTextWithTooltip from "@/components/CollapseTextWithTooltip.vue";

export default {
  name: 'preview-talent-matrix',
  components: {
    TalentMatrixCellEditDialog,
    CollapseTextWithTooltip,
  },
  props: {
    matrix: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      showMatrix: false,
      matrix_data: [],
      dragSource: null,
      draggedCell: null,
      hoverTarget: null,
      dragCounter: {},
    }
  },
  methods: {
    openEditDialog(cell = {}) {
      this.$refs.editDialog.open(cell);
    },
    cellUpdate() {
      this.$emit('update-matrix');
    },
    getCell(i, j) {
      let cell = this.matrix.cells.find(
        (item) => item.row === i && item.column === j
      );

      if (cell) {
        this.showMatrix = true;
      }

      return cell;
    },
    onDragStart(row, column) {
      this.dragSource = { row, column };
      this.draggedCell = { row, column };
    },
    onDragEnter(row, column) {
      const key = `${row}-${column}`;
      this.dragCounter[key] = (this.dragCounter[key] || 0) + 1;
      this.hoverTarget = { row, column };
    },
    onDragLeave(row, column) {
      const key = `${row}-${column}`;
      if (this.dragCounter[key]) {
        this.dragCounter[key]--;
        if (this.dragCounter[key] === 0) {
          if (this.hoverTarget?.row === row && this.hoverTarget?.column === column) {
            this.hoverTarget = null;
          }
        }
      }
    },
    onDragEnd() {
      this.draggedCell = null;
      this.hoverTarget = null;
    },
    onDragOver(row, column) {
      if (
        this.dragSource &&
        (this.dragSource.row !== row || this.dragSource.column !== column)
      ) {
        this.hoverTarget = { row, column };
      }
    },
    async onDrop(targetRow, targetColumn) {
      if (!this.dragSource) return;

      const sourceCell = this.getCell(this.dragSource.row, this.dragSource.column);
      const targetCell = this.getCell(targetRow, targetColumn);
      if (!sourceCell || !targetCell) return;

      [sourceCell.row, targetCell.row] = [targetCell.row, sourceCell.row];
      [sourceCell.column, targetCell.column] = [targetCell.column, sourceCell.column];

      this.dragSource = null;
      this.draggedCell = null;
      this.hoverTarget = null;

      await this.updateMatrixOrder();
    },
    async updateMatrixOrder() {
      const cellsOrder = this.matrix.cells.map(cell => ({
        id: cell.id,
        row: cell.row,
        column: cell.column
      }));

      await this.$api.talentMatrix.matrixCellPositionsUpdate({
        cells: cellsOrder,
      })
    }
  },
}
</script>

<style scoped lang="scss">
  .preview-matrix {
    .row {
      display: flex;
    }

    .description-cell {
      display: flex;
      align-items: center;
    }

    .matrix {
      overflow: auto;
    }

    .cell {
      cursor: move;
      box-shadow: rgba(0, 0, 0, 0.1) 0 0 10px;
      border: 1px solid rgb(225, 225, 225);
      width: 205px;
      height: 150px;
      margin: 10px;
      background-color: white;
      border-radius: 7px;
      position: relative;
    }

    .name {
      cursor: pointer;
      font-size: 15px;
    }

    .header-cell {
      min-height: 28px;
      display: flex;
      justify-content: space-between;
      border-bottom: 1px solid #EBEEF5;
      background-color: rgba(255, 255, 255, 0.7);
      padding: 5px 10px;
    }

    .matrix-block {
      margin: 0 25px 25px 0px;
      position: relative;
    }

    .potential_side span,
    .productivity_side span {
      font-size: 36px;
      font-weight: bold;
    }

    .potential_side {
      color: #9a9a9a;
      position: absolute;
      left: -107px;
      bottom: 47px;
      transform: rotate(-90deg);
      font-size: 25px;
    }

    .productivity_side {
      color: #9a9a9a;
      position: absolute;
      bottom: -60px;
      font-size: 25px;
      left: 14px;
    }

    .move-icon {
      color: #8a8a8a;
      opacity: 0.5;
      border-radius: 7px;
      font-size: 45px;
      padding: 10px 15px;
      position: absolute;
      top: 63%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: rgba(255, 255, 255, 0.7);
    }

    .cell.dragging {
      box-shadow: 0 0 20px rgba(0, 150, 255, 0.7);
      opacity: 0.5;
      transform: scale(1.03);
      transition: all 0.2s ease;
    }

    .cell.drop-target {
      outline: 3px dashed rgba(0, 150, 255, 0.8);
      box-shadow: 0 0 15px rgba(0, 150, 255, 0.5);
      transition: all 0.2s ease;
    }
  }
</style>
